import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Toaster } from 'react-hot-toast';
import { auth } from './firebaseConfig';
import { SkipsDataProvider } from './context/SkipsDataContext';
import { BatchedUpdatesProvider } from './context/BatchedUpdatesContext';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './components/LandingPage';
import NewCustomer from './components/NewCustomer';
import MapComponent from './components/MapComponent';
import RecordsPage from './components/RecordsPage';
import LogPage from './components/LogPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import DriverManagement from './components/DriverManagement';
import SuperAdminPanel from './components/SuperAdminPanel';
import ClientSetup from './components/ClientSetup';
import SignupForm from './components/SignupForm';
import SignupConfirmation from './components/SignupConfirmation';
import Inventory from './components/Inventory';
import { Analytics } from '@vercel/analytics/react';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div style={{
          fontSize: '20px',
          color: '#3B82F6'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  return (
    <>
    <ErrorBoundary>
      <Router>
        <SkipsDataProvider>
          <BatchedUpdatesProvider debounceMs={250}>
            <Toaster
            position="top-right"
            toastOptions={{
              success: {
                duration: 3000,
                style: {
                  background: '#10B981',
                  color: 'white',
                },
              },
              error: {
                duration: 4000,
                style: {
                  background: '#EF4444',
                  color: 'white',
                },
              },
              loading: {
                style: {
                  background: '#3B82F6',
                  color: 'white',
                },
              },
            }}
          />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/signup" element={<SignupForm />} />
            <Route path="/signup-confirmation" element={<SignupConfirmation />} />
            <Route path="/setup" element={<ClientSetup />} />
            <Route path="/new-customer" element={<NewCustomer />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/map" element={
              <ProtectedRoute>
                <MapComponent />
              </ProtectedRoute>
            } />
            <Route path="/records" element={
              <ProtectedRoute>
                <RecordsPage />
              </ProtectedRoute>
            } />
            <Route path="/log" element={
              <ProtectedRoute>
                <LogPage />
              </ProtectedRoute>
            } />
            <Route path="/drivers" element={
              <ProtectedRoute>
                <DriverManagement />
              </ProtectedRoute>
            } />
            <Route path="/inventory" element={
              <ProtectedRoute>
                <Inventory />
              </ProtectedRoute>
            } />
            <Route path="/super-admin" element={
              <ProtectedRoute>
                <SuperAdminPanel />
              </ProtectedRoute>
            } />
            </Routes>
          </BatchedUpdatesProvider>
        </SkipsDataProvider>
      </Router>
    </ErrorBoundary>
    <Analytics />
    </>
  );
}

export default App;